import { useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTours } from "@/hooks/useTours";
import { tourUrl, companyUrl } from "@/lib/slugify";
import { getListingConfig, boatTypeUrl, CATEGORY_LISTINGS } from "@/lib/listingSlugs";
import { getLowestPrice, getAggregatedReview } from "@/lib/tourUtils";
import { breadcrumbJsonLd, jsonLdScript } from "@/lib/seoSchemas";
import type { Tour } from "@/types/tour";
import { BOAT_TYPE_OPTIONS } from "@/types/tour";
import { Star, Ship, ChevronRight, Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";

/* ── JSON-LD ─────────────────────────────────────────── */
function ListingJsonLd({ config, tours }: { config: ReturnType<typeof getListingConfig>; tours: Tour[] }) {
  if (!config) return null;
  const prices = tours.map(getLowestPrice).filter((p): p is number => p !== null);
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: config.metaTitle,
    description: config.metaDesc,
    url: `https://topcarib.com/aruba/${config.slug}`,
    ...(prices.length > 0 && {
      offers: {
        "@type": "AggregateOffer",
        lowPrice: Math.min(...prices),
        highPrice: Math.max(...prices),
        priceCurrency: "USD",
        offerCount: tours.length,
      },
    }),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />;
}

/* ── All Tours Footer ────────────────────────────────── */
function AllBoatTours({ tours, highlightFilter }: { tours: Tour[]; highlightFilter: (t: Tour) => boolean }) {
  const grouped = useMemo(() => {
    const active = tours.filter((t) => t.is_active);
    const groups: Record<string, Tour[]> = {};
    active.forEach((t) => {
      const type = t.boat_type || "Other";
      if (!groups[type]) groups[type] = [];
      groups[type].push(t);
    });
    const order = [...BOAT_TYPE_OPTIONS, "Other"];
    return order.filter((bt) => groups[bt]).map((bt) => ({ type: bt, tours: groups[bt] }));
  }, [tours]);

  const companies = useMemo(() => {
    const active = tours.filter((t) => t.is_active && t.company_name);
    return [...new Set(active.map((t) => t.company_name!))].sort();
  }, [tours]);

  return (
    <section className="mt-16 pb-12">
      <Separator className="mb-12" />
      <h2 className="font-display text-2xl font-bold text-foreground mb-8">All Boat Tours in Aruba</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {grouped.map(({ type, tours: groupTours }) => (
          <div key={type}>
            <Link to={boatTypeUrl(type)} className="font-display text-lg font-semibold text-foreground mb-3 flex items-center gap-2 hover:text-primary transition-colors">
              <Ship className="w-4 h-4 text-primary" />
              {type} Tours
            </Link>
            <ul className="space-y-1.5">
              {groupTours.map((t) => (
                <li key={t.id}>
                  <Link
                    to={tourUrl(t.id, t.title)}
                    className={`text-xs hover:underline ${highlightFilter(t) ? "text-foreground font-medium" : "text-primary"}`}
                  >
                    {t.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Categories */}
      <Separator className="my-12" />
      <h2 className="font-display text-2xl font-bold text-foreground mb-8">Aruba Boat Tours by Category</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-2">
        {CATEGORY_LISTINGS.map((c) => (
          <Link key={c.slug} to={`/aruba/${c.slug}`} className="text-xs text-primary hover:underline">
            {c.value} Tours
          </Link>
        ))}
      </div>

      <Separator className="my-12" />
      <h2 className="font-display text-2xl font-bold text-foreground mb-8">Boat Tour Companies in Aruba</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-2">
        {companies.map((name) => (
          <Link key={name} to={companyUrl(name)} className="text-xs text-primary hover:underline">
            {name}
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ── Tour Card ───────────────────────────────────────── */
function TourCard({ tour }: { tour: Tour }) {
  const price = getLowestPrice(tour);
  const review = getAggregatedReview(tour);

  return (
    <Link to={tourUrl(tour.id, tour.title)} className="group block">
      <div className="bg-card rounded-xl border border-border overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        {tour.cover_photo_url ? (
          <div className="aspect-[16/10] overflow-hidden">
            <img
              src={tour.cover_photo_url}
              alt={tour.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-muted flex items-center justify-center">
            <Ship className="w-8 h-8 text-muted-foreground" />
          </div>
        )}
        <div className="p-4">
          <h3 className="font-display font-bold text-sm leading-tight line-clamp-2">{tour.title}</h3>
          {tour.company_name && (
            <p className="text-xs text-muted-foreground mt-1">{tour.company_name}</p>
          )}
          <div className="flex items-center justify-between mt-3">
            {price !== null && <span className="font-display font-bold text-primary">From ${price}</span>}
            {review && (
              <span className="flex items-center gap-1 text-xs">
                <Star className="w-3.5 h-3.5 fill-warning text-warning" />
                <span className="font-semibold">{review.rating.toFixed(1)}</span>
                <span className="text-muted-foreground">({review.count})</span>
              </span>
            )}
          </div>
          {tour.duration_hours !== null && (
            <p className="text-xs text-muted-foreground mt-2">
              {tour.duration_hours === -1 ? "Variable duration" : `${tour.duration_hours}h`}
              {tour.boat_type && ` · ${tour.boat_type}`}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
}

/* ── Main Page ───────────────────────────────────────── */
export default function ListingPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: tours, isLoading } = useTours();

  const config = slug ? getListingConfig(slug) : undefined;

  const { filteredTours, allTours } = useMemo(() => {
    if (!tours || !config) return { filteredTours: [], allTours: [] };
    const active = tours.filter((t) => t.is_active);
    const filtered = active.filter((t) => {
      if (config.type === 'category') {
        return t.categories?.includes(config.value as any);
      }
      return t.boat_type === config.value;
    });
    return { filteredTours: filtered, allTours: active };
  }, [tours, config]);

  // SEO meta
  useEffect(() => {
    if (!config) return;
    document.title = config.metaTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", config.metaDesc);
    else {
      const m = document.createElement("meta");
      m.name = "description";
      m.content = config.metaDesc;
      document.head.appendChild(m);
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.href = `https://topcarib.com/aruba/${config.slug}`;
  }, [config]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <h1 className="font-display text-2xl font-bold text-foreground">Page not found</h1>
        <Link to="/" className="text-primary hover:underline">← Back to all tours</Link>
      </div>
    );
  }

  const prices = filteredTours.map(getLowestPrice).filter((p): p is number => p !== null);
  const minPrice = prices.length > 0 ? Math.min(...prices) : null;

  const highlightFilter = (t: Tour) => {
    if (config.type === 'category') return !!t.categories?.includes(config.value as any);
    return t.boat_type === config.value;
  };

  const breadcrumb = breadcrumbJsonLd([
    { name: "Home", url: "/" },
    { name: "Things to do in Aruba", url: "/aruba/things-to-do" },
    { name: config.h1, url: `/aruba/${config.slug}` },
  ]);

  return (
    <div className="min-h-screen bg-background">
      <ListingJsonLd config={config} tours={filteredTours} />
      <script type="application/ld+json" dangerouslySetInnerHTML={jsonLdScript(breadcrumb)} />

      {/* Navbar */}
      <nav className="bg-card border-b border-border px-6 lg:px-10 py-3 flex items-center justify-between z-50">
        <Link to="/" className="flex items-center">
          <span className="font-display text-xl font-extrabold text-warning tracking-tight">Top</span>
          <span className="font-display text-xl font-extrabold text-primary tracking-tight">Carib</span>
        </Link>
        <Link to="/" className="text-sm text-primary hover:underline flex items-center gap-1">
          ← All tours
        </Link>
      </nav>

      {/* Breadcrumb */}
      <div className="px-6 lg:px-10 py-3 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
        <ChevronRight className="w-3.5 h-3.5 inline mx-1" />
        <span className="text-foreground font-medium">{config.h1}</span>
      </div>

      {/* Hero */}
      <header className="px-6 lg:px-10 pb-8">
        <div className="max-w-4xl">
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-foreground leading-tight">
            {config.h1}
          </h1>
          <p className="text-foreground/70 mt-3 text-base md:text-lg max-w-2xl">
            {config.description}
          </p>
          <div className="flex items-center gap-4 mt-3">
            <span className="text-sm text-muted-foreground">{filteredTours.length} {filteredTours.length === 1 ? "tour" : "tours"} available</span>
            {minPrice !== null && (
              <span className="text-primary font-display font-bold text-lg">Starting from ${minPrice}</span>
            )}
          </div>
        </div>
      </header>

      {/* Tour Grid */}
      <main className="px-6 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredTours.map((tour) => (
            <TourCard key={tour.id} tour={tour} />
          ))}
        </div>

        <AllBoatTours tours={allTours} highlightFilter={highlightFilter} />
      </main>
    </div>
  );
}
